import "./ProspectionsTools.scss";
import AddProspectionModal from "components/modal/AddProspectionModal";
import Button from "core/buttons/button/Button";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import ScrollToTop from "core/buttons/scroll-buttons/ScrollToTop";
import NoNesultIcon from "assets/icons/no-result.svg";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import {
  FILTERS_FOR_NAMES,
  LANG,
  PROSPECTION_DEFAULT_COLORS,
  QUERY_STATUS,
  TOGGLE_SWITCH_STATUS,
} from "constants/constants";
import ToggleSwitchButton from "core/buttons/toggle-switch-button/ToggleSwitchButton";
import { getFormattedDate } from "utils/functions";
import InfoContext from "pages/InfoContext";
import { renderInitialLoaderOrNoResult } from "utils/toolsManagerHelper";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { getEntityIsAllowed } from "utils/helper";

const ProspectionsToolsByFilter = () => {
  const {
    createProspection,
    lang,
    prospectionList,
    getProspectionStatus,
    askAssignmentById,
    userScope,
    setValidationModalMessage,
  } = useContext(InfoContext);

  const [modal, setModal] = useState(false);
  const { clients = [], prospections = [] } = prospectionList;
  const { getNamesByFilters, namesByFilters } = useGeneralContext();

  useEffect(() => {
    const ids = [...clients, ...prospections]
      ?.map((e) => e?.id)
      .filter(Boolean);
    if (ids?.length) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.CLIENT],
        id: ids,
      });
    }
  }, [clients, prospections]);

  const openAddProspectionModal = () => {
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
  };

  const askAssignmentHandler = (table, id, hasManager) => {
    setValidationModalMessage({
      successMessageId: hasManager
        ? "on_hold_client_assignment"
        : "success_ask_client_assignment",
      successMessageDefault: hasManager
        ? "Votre demande d'attribution a bien été envoyée."
        : "Vous êtes autorisés à travailler avec ce client.",
    });
    id && askAssignmentById({ id, assignment_type: table });
  };

  const createColumn = (id, defaultMessage, icon, width, options = {}) => ({
    label: {
      key: id,
      value: (
        <FormattedMessage id={`${id}_label`} defaultMessage={defaultMessage} />
      ),
    },
    icon: icon || "user",
    iconWidth: options.iconWidth || 20,
    iconHeight: options.iconHeight || 20,
    id,
    width,
    ...options,
  });

  const header = [
    createColumn("client-name", "Nom", "user", "25%"),
    createColumn("managers", "Managers", "user", "20%"),
    createColumn("since-date", "Depuis le", "calendar", "10%"),
    createColumn("billings-number", "Nombre de facturés", "user", "12%"),
    createColumn("attribution", "Attribution", "user", "18%"),
    createColumn("answer", "Réponse", "user", "15%"),
  ];

  const responses = {
    "NO": { fr: "Non", en: "No" },
    "N/A": { fr: "N/A", en: "N/A" },
    "on-hold": { fr: "En attente", en: "On hold" },
    "Yes": { fr: "Oui", en: "Yes" },
    "done": { fr: "Fait", en: "Done" },
  };

  const formattedResponse = (response) =>
    lang === LANG.FR ? responses[response]?.fr : responses[response]?.en;

  const toggleSwitchColor = {
    green: TOGGLE_SWITCH_STATUS.ON,
    orange: TOGGLE_SWITCH_STATUS.NA,
    red: TOGGLE_SWITCH_STATUS.OFF,
  };

  const formattedContent = ({ data, table }) =>
    data?.map((e) => {
      const content = {
        client:
          table === FILTERS_FOR_NAMES.CLIENT &&
          getEntityIsAllowed(e?.id, namesByFilters) !== false ? (
            <Link
              to={`/${userScope}/client/${e?.slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {e?.name}
            </Link>
          ) : (
            e?.name
          ),
        managers: e?.managers?.map((manager) => (
          <div key={`manager-${manager?.id}`} className="managers color-option">
            <div className="manager-name">{manager?.name}</div>
            <ToggleSwitchButton
              index={`manager-${manager?.slug}`}
              value={toggleSwitchColor[manager?.traffic_color]}
              disabled
            />
          </div>
        )),
        since: e?.managers.map((manager) => (
          <p key={`since-${manager?.slug}`}>
            {getFormattedDate(manager?.since, lang) || "-"}
          </p>
        )),
        ...(table === FILTERS_FOR_NAMES.CLIENT && {
          billings_number: e?.managers?.map((manager) => (
            <p key={`current_mission_count-${manager?.slug}`}>
              {manager?.current_mission_count}
            </p>
          )),
        }),
        attribution_requested:
          !e?.attribution_requested &&
          renderAskAttributionButton({
            id: e?.id,
            table,
            hasManager: e.managers?.length > 0,
          }),
        response: e?.managers?.map((manager) => (
          <p key={`response-${manager?.slug}`}>
            {formattedResponse(manager?.response) || "-"}
          </p>
        )),
      };

      return content;
    });

  const renderAskAttributionButton = ({ id, table, hasManager }) => (
    <div
      className="btn btn-common btn-medium
      white"
      style={{ padding: "unset", marginBottom: "12px" }}
    >
      <Button
        color="btn-medium btn-outlined "
        style={{ padding: "unset" }}
        onClick={() => askAssignmentHandler(table, id, hasManager)}
        id="button-search"
      >
        <FormattedMessage
          id="ask_attribution_label"
          defaultMessage="Demander l'attribution"
        />
      </Button>
    </div>
  );

  const renderResultsBySection = ({ table, data }) => {
    const sectionTitle = (
      <div
        className="display-flex"
        style={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={
            <FormattedMessage
              id={`search_${table}_label`}
              defaultMessage={`Recherche de ${
                table === FILTERS_FOR_NAMES.CLIENT
                  ? "(ex) clients"
                  : "prospections"
              }`}
            />
          }
        />
        {table === FILTERS_FOR_NAMES.CLIENT && data?.length > 0 && (
          <div className="colored-toggles">
            {PROSPECTION_DEFAULT_COLORS.map((e) => e?.component)}
          </div>
        )}
      </div>
    );

    return (
      <div className={`search-${table}s-section`}>
        {data?.length === 0 && getProspectionStatus === QUERY_STATUS.SUCCESS && (
          <>
            {sectionTitle}
            <div className="no-result-wrapper">
              <img
                src={NoNesultIcon}
                alt="no-result-icon"
                className="no-result-icon"
              />
              <p className="title-4 violet no-result-text">
                <FormattedMessage
                  id="no_results"
                  defaultMessage="Oops, aucun résultat pour cette recherche."
                />
              </p>
            </div>
          </>
        )}
        {data?.length > 0 && (
          <>
            {sectionTitle}
            <Table
              id={`prospection-${table}-by-filter`}
              header={
                table === FILTERS_FOR_NAMES.CLIENT
                  ? header
                  : header.filter((elm) => elm.id !== "billings-number")
              }
              content={formattedContent({
                data,
                table,
              })}
              tableWidth={table === FILTERS_FOR_NAMES.CLIENT ? "100%" : "88%"}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="section">
      <ScrollToTop />
      {renderInitialLoaderOrNoResult({
        status: getProspectionStatus,
        hasContent: [...clients, ...prospections]?.length,
      })}
      {modal && (
        <AddProspectionModal
          closeModal={closeModal}
          createProspection={createProspection}
        />
      )}

      {renderResultsBySection({
        table: FILTERS_FOR_NAMES.CLIENT,
        data: clients,
      })}

      {renderResultsBySection({
        table: FILTERS_FOR_NAMES.PROSPECTION,
        data: prospections,
      })}

      <div className="add_prospection_button">
        <Button
          color="btn-medium gray-700"
          onClick={() => openAddProspectionModal()}
          id="button-search"
        >
          <FormattedMessage
            id="add_prospection_button_tools"
            defaultMessage="Ajouter une prospection"
          />
        </Button>
      </div>
    </div>
  );
};

export default ProspectionsToolsByFilter;
